/**
 * Cloudinary URL generation utilities
 * 
 * We store public IDs instead of full URLs to enable:
 * - Dynamic image resizing
 * - Automatic format optimization (WebP, AVIF)
 * - Quality adjustments
 * - Face-centered cropping for avatars
 */

// Default cloud name - will be overridden by config from backend
let cloudName = '';

/**
 * Set the Cloudinary cloud name (called once when config is loaded)
 */
export function setCloudName(name) {
  cloudName = name;
}

/**
 * Get the current cloud name
 */
export function getCloudName() {
  return cloudName;
}

/**
 * Generate a Cloudinary URL from a public ID
 * 
 * @param {string} publicId - The Cloudinary public ID
 * @param {Object} options - Transformation options
 * @param {number} options.width - Target width
 * @param {number} options.height - Target height
 * @param {string} options.crop - Crop mode: 'fill', 'thumb', 'fit', etc.
 * @param {string} options.gravity - Focus point: 'face', 'auto', 'center', etc.
 * @param {string} options.quality - Quality: 'auto', 'auto:best', 'auto:low', or 1-100
 * @param {string} options.format - Format: 'auto', 'webp', 'jpg', 'png', etc.
 * @returns {string} The full Cloudinary URL
 */
export function getCloudinaryUrl(publicId, options = {}) {
  if (!publicId || !cloudName) {
    return null;
  }

  // Check if it's already a full URL (legacy support)
  if (publicId.startsWith('http://') || publicId.startsWith('https://')) {
    return publicId;
  }

  const {
    width,
    height,
    crop = 'fill',
    gravity = 'face',
    quality = 'auto',
    format = 'auto',
  } = options;

  // Build transformation string
  const transforms = [];
  
  if (width) transforms.push(`w_${width}`);
  if (height) transforms.push(`h_${height}`);
  if (crop) transforms.push(`c_${crop}`);
  if (gravity) transforms.push(`g_${gravity}`);
  if (quality) transforms.push(`q_${quality}`);
  if (format) transforms.push(`f_${format}`);

  const transformString = transforms.length > 0 ? transforms.join(',') + '/' : '';

  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformString}${publicId}`;
}

/**
 * Generate an avatar URL with face-detection cropping
 * 
 * @param {string} publicId - The Cloudinary public ID
 * @param {number} size - Avatar size in pixels (default: 100)
 * @returns {string} The optimized avatar URL
 */
export function getAvatarUrl(publicId, size = 100) {
  // For retina displays, we request 2x the size
  const retinaSize = size * 2;
  
  return getCloudinaryUrl(publicId, {
    width: retinaSize,
    height: retinaSize,
    crop: 'thumb',
    gravity: 'face',
    quality: 'auto',
    format: 'auto',
  });
}

/**
 * Generate a larger profile picture URL
 *
 * @param {string} publicId - The Cloudinary public ID
 * @param {number} size - Image size in pixels (default: 400)
 * @returns {string} The optimized profile picture URL
 */
export function getProfilePictureUrl(publicId, size = 400) {
  // For retina displays, we request 2x the size
  const retinaSize = size * 2;

  return getCloudinaryUrl(publicId, {
    width: retinaSize,
    height: retinaSize,
    crop: 'fill',
    gravity: 'face',
    quality: 'auto:good',
    format: 'auto',
  });
}

export default {
  setCloudName,
  getCloudName,
  getCloudinaryUrl,
  getAvatarUrl,
  getProfilePictureUrl,
};

